import type { ReactNode } from 'react';
import { useT } from '@dia/i18n';
import { StatusPill, type StatusTone } from './StatusPill';

/** A check sized to the text, in the pill's own color. */
function CheckGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1.1em"
      height="1.1em"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="block"
      focusable="false"
    >
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

export type SyncBadgeProps = {
  /** How many writes are still sitting in the outbox. */
  pending: number;
  /**
   * How many the island refused for good, parked out of the queue's way
   * (defect D5). This outranks everything else in the badge: a queue that is
   * otherwise empty while a write is stuck is the state that used to be
   * invisible.
   */
  dead?: number;
  online: boolean;
  /** True while a drain is running. */
  syncing?: boolean;
  className?: string;
};

/**
 * The one honest indicator of whether the island has swallowed your work yet.
 * Offline with nothing pending is still a calm state, not an error.
 */
export function SyncBadge({ pending, dead = 0, online, syncing = false, className }: SyncBadgeProps) {
  const { t } = useT();

  // The calm state (online, nothing queued, nothing stuck) says nothing,
  // rather than a permanent "Everything is sent" pill sitting in the header
  // on every screen for something that is true almost all of the time.
  if (dead === 0 && online && !syncing && pending === 0) return null;

  let tone: StatusTone = 'ok';
  let label = t('sync.all_sent');
  // A drawn check rather than the letters "OK", which said the same thing as
  // the word beside it twice over. The word still carries the meaning; the
  // check is decoration, so the pill stays aria-hidden on it.
  let glyph: ReactNode = <CheckGlyph />;

  if (dead > 0) {
    tone = 'danger';
    label = t('sync.dead_count', { count: dead });
    glyph = 'X';
  } else if (!online) {
    tone = pending > 0 ? 'warn' : 'neutral';
    label = pending > 0 ? t('sync.pending_count', { count: pending }) : t('sync.offline');
    glyph = '!';
  } else if (syncing) {
    tone = 'info';
    label = t('sync.sending');
    glyph = '...';
  } else if (pending > 0) {
    tone = 'warn';
    label = t('sync.pending_count', { count: pending });
    glyph = '!';
  }

  return (
    <StatusPill tone={tone} glyph={glyph} className={className} title={label}>
      {label}
    </StatusPill>
  );
}
